/**
 * AwaitingReceptionsTab — Section Stock > Entrées > À réceptionner
 *
 * Liste les achats Caisse Pro VALIDÉS mais pas encore RÉCEPTIONNÉS
 * physiquement. Le magasinier confirme la réception via un bouton, ce qui
 * déclenche la mise en stock (endpoint /api/expenses/{id}/receive-stock).
 *
 * Traçabilité :
 *   - N° de dépense, fournisseur, montant, date de validation
 *   - Détail des articles (accordéon)
 *   - Bouton "Confirmer la réception" (magasinier / admin)
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  PackageCheck,
  ChevronDown,
  ChevronRight,
  Truck,
  Package,
  RefreshCw,
  Loader2,
  ShoppingCart,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (p) => new Intl.NumberFormat("fr-FR").format(p || 0);

const AwaitingReceptionsTab = ({ currentUser }) => {
  const [expenses, setExpenses] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [receiving, setReceiving] = useState({});

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock/receptions/awaiting`);
      setExpenses(r.data?.expenses || []);
      setTotalAmount(r.data?.total_amount || 0);
    } catch (e) {
      toast.error("Erreur chargement des réceptions en attente");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const confirmReception = async (exp) => {
    if (!window.confirm(
      `Confirmer la réception physique de cet achat ?\n\n` +
      `${exp.description || "(sans description)"}\n` +
      `Montant : ${fmt(exp.amount)} F CFA\n\n` +
      `Cela ajoutera automatiquement les articles au stock et créera les mouvements d'entrée.`
    )) return;
    setReceiving((s) => ({ ...s, [exp.id]: true }));
    try {
      const r = await axios.post(
        `${API}/expenses/${exp.id}/receive-stock`,
        { user_name: currentUser?.full_name || currentUser?.username || "Magasinier" }
      );
      if (r.data?.already_received) {
        toast.info("Déjà réceptionné auparavant.");
      } else {
        toast.success(`✅ Réception confirmée — ${r.data?.received_items || 0} article(s) ajouté(s) au stock`);
      }
      fetchList();
      // Déclenche un event pour rafraîchir les autres vues Stock (queue portionnement, etc.)
      window.dispatchEvent(new CustomEvent("stock-reception-confirmed", { detail: { expense_id: exp.id } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur pendant la réception");
    } finally {
      setReceiving((s) => ({ ...s, [exp.id]: false }));
    }
  };

  const toggleExpand = (id) => {
    setExpanded((s) => ({ ...s, [id]: !s[id] }));
  };

  return (
    <div className="space-y-3" data-testid="awaiting-receptions-tab">
      {/* Header KPIs */}
      <Card className="bg-gradient-to-br from-amber-950/40 to-orange-950/30 border-amber-500/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-amber-200 flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5" />
              Achats validés — En attente de réception physique
              <Badge className="bg-amber-500/30 text-amber-100 text-[10px]">
                {expenses.length}
              </Badge>
            </span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-amber-200 font-mono">
                Total : <strong>{fmt(totalAmount)} F</strong>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchList}
                disabled={loading}
                className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                data-testid="receptions-refresh-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-amber-200/80 pt-2 border-t border-amber-500/20">
          Ces achats ont été <strong>validés par l&apos;administrateur</strong> dans Caisse Pro et
          doivent être physiquement reçus. Une fois confirmés ici, les articles seront
          ajoutés au stock et pourront passer en <strong>file de portionnement</strong> si le produit le nécessite.
        </CardContent>
      </Card>

      {/* Liste */}
      {expenses.length === 0 && !loading ? (
        <Card className="bg-slate-800/30 border-slate-700">
          <CardContent className="py-12 text-center">
            <PackageCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 text-base">Aucun achat en attente de réception 🎉</p>
            <p className="text-slate-600 text-xs mt-2">
              Tous les achats validés ont été physiquement reçus. Les nouveaux achats validés dans <strong>Caisse Pro</strong> apparaîtront ici.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {expenses.map((e) => {
            const isExpanded = !!expanded[e.id];
            const isReceiving = !!receiving[e.id];
            const items = e.items || [];
            return (
              <Card
                key={e.id}
                className="bg-slate-800/40 border-slate-700 hover:border-amber-500/40 transition-colors"
                data-testid={`awaiting-expense-${e.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => toggleExpand(e.id)}
                      className="text-slate-400 hover:text-white p-1"
                      data-testid={`expand-${e.id}`}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold truncate max-w-md">
                          {e.description || "(sans description)"}
                        </span>
                        {e.supplier && (
                          <Badge className="bg-slate-700 text-slate-200 text-[10px]">
                            <Truck className="w-3 h-3 mr-1" /> {e.supplier}
                          </Badge>
                        )}
                        {e.category && (
                          <Badge className="bg-slate-700/60 text-slate-300 text-[10px]">
                            {e.category}
                          </Badge>
                        )}
                        {e.stock_reception_status === "expected" && (
                          <Badge className="bg-amber-500/20 text-amber-300 text-[10px] border border-amber-500/40">
                            Attendu
                          </Badge>
                        )}
                        <Badge className="bg-cyan-500/10 text-cyan-300 text-[10px] border border-cyan-500/40">
                          Validé Caisse
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-1 flex-wrap">
                        {e.approved_at && (
                          <span>Validé : <strong className="text-slate-300">{
                            (() => { try { return format(parseISO(e.approved_at), "dd/MM/yyyy HH:mm", { locale: fr }); } catch { return e.approved_at.slice(0,16); } })()
                          }</strong></span>
                        )}
                        {e.requested_by && (
                          <span>Demandé : <strong className="text-slate-300">{e.requested_by}</strong></span>
                        )}
                        {items.length > 0 && (
                          <span className="text-indigo-300"><Package className="w-3 h-3 inline mr-1" />{items.length} article{items.length > 1 ? "s" : ""}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-amber-300 font-mono">{fmt(e.amount)} F</div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => confirmReception(e)}
                      disabled={isReceiving}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`confirm-reception-${e.id}`}
                    >
                      {isReceiving ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Réception…</>
                      ) : (
                        <><PackageCheck className="w-4 h-4 mr-1" /> Confirmer réception</>
                      )}
                    </Button>
                  </div>

                  {/* Détails des articles */}
                  {isExpanded && items.length > 0 && (
                    <div className="mt-3 bg-slate-900/60 rounded p-3 border border-slate-700/50">
                      <p className="text-slate-300 text-[11px] uppercase font-bold mb-2 flex items-center gap-1">
                        <ShoppingCart className="w-3 h-3" /> Articles à réceptionner
                      </p>
                      <div className="space-y-1">
                        {items.map((it, idx) => (
                          <div key={idx} className={`flex items-center justify-between text-sm py-1 px-2 rounded ${it.struck ? "opacity-40 line-through" : "hover:bg-slate-800/40"}`}>
                            <span className="text-white flex-1 truncate">
                              {idx + 1}. {it.description || "—"}
                              {it.unit && (
                                <span className="text-slate-500 ml-2 text-[10px]">({it.unit})</span>
                              )}
                            </span>
                            <span className="text-slate-400 text-[11px] whitespace-nowrap ml-3">
                              {it.quantity} × {fmt(it.unit_price)} = <strong className="text-amber-300">{fmt(it.amount)} F</strong>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AwaitingReceptionsTab;
